/**
 * Recharge API token scopes Kim needs. machine checks the same lists on connect
 * (backend/helpers/recharge.js) — keep the two in step.
 *
 * A key missing a read scope is rejected; a key missing a write scope connects, and the
 * actions that scope backs fail until it is added.
 */
export const RECHARGE_READ_SCOPES = ['read_customers', 'read_subscriptions', 'read_orders', 'read_store'];

export const RECHARGE_WRITE_SCOPES = ['write_subscriptions', 'write_orders', 'write_payment_methods'];

export const RECHARGE_API_TOKEN_GUIDE_URL = 'https://docs.getrecharge.com/docs/recharge-api-key';

/**
 * The same scopes as the rows of Recharge's "Create an API Token" form: each row takes
 * "No access", "Read access" or "Read and Write access".
 */
export const RECHARGE_TOKEN_PERMISSIONS = [
	{ resource: 'Customers', access: 'Read access', scopes: ['read_customers'] },
	{ resource: 'Subscriptions', access: 'Read and Write access', scopes: ['read_subscriptions', 'write_subscriptions'] },
	{ resource: 'Orders', access: 'Read and Write access', scopes: ['read_orders', 'write_orders'] },
	{ resource: 'Store', access: 'Read access', scopes: ['read_store'] },
	{ resource: 'Payment methods', access: 'Read and Write access', scopes: ['write_payment_methods'] }
];

/** What Kim cannot do on Recharge without each write scope. */
const WRITE_SCOPE_ACTIONS: Record<string, string> = {
	write_subscriptions: 'cancel, reactivate, reschedule or re-address subscriptions, add one-time products',
	write_orders: 'skip, discount or refund charges',
	write_payment_methods: 'refund charges'
};

/** machine's connect response. Both fields are absent on a deployment without the scope check. */
export interface RechargeConnectResult {
	scopesVerified?: boolean;
	missingScopes?: string[];
}

/** The scopes machine reported missing on a rejected connect, or none. */
export const readMissingScopes = (error: unknown): string[] => {
	const missingScopes = (error as { response?: { data?: { missingScopes?: unknown } } })?.response?.data?.missingScopes;
	return Array.isArray(missingScopes) ? missingScopes.filter((scope): scope is string => typeof scope === 'string') : [];
};

/** Each missing write scope with the actions it blocks, or '' when none is missing. */
export const describeMissingWriteScopes = (missingScopes: string[]): string =>
	missingScopes
		.filter((scope) => WRITE_SCOPE_ACTIONS[scope])
		.map((scope) => `${scope} (${WRITE_SCOPE_ACTIONS[scope]})`)
		.join(', ');
